import {
  ContentBuilderCompiledBloc,
  ContentBuilderCompiledPage,
  ContentBuilderCompiledSpec,
  ContentBuilderSpecApiResponse,
  ContentBuilderSpecRuntimePayload,
} from './content-builder-spec.model';
import {
  IBlockConfig,
  IMarketplaceUserInterface,
  IPageConfig,
  DoodModalConfig,
} from '@core/models/marketplace.model';
import { mapColorsParam } from './content-builder-prop-format.util';
import { materializeColorPaletteParamValues } from './materialize-color-palette-param-values.util';

const ROUTING_SLOTS = new Set(['pages']);
const MOSAIC_BLOCK_TYPES = new Set(['MosaicBlockComponent']);
const DEFAULT_PAGE_GUARDS_BY_COMPONENT_TYPE: Readonly<Record<string, readonly string[]>> = {
  CartPageComponent: ['AuthGuard'],
  CartFunnelPageComponent: ['AuthGuard'],
  ValidateSharedOrderPageComponent: ['AuthGuard'],
  PaymentPageComponent: ['AuthGuard', 'OrderValidateGuard'],
  PaymentConfirmPageComponent: ['AuthGuard'],
  GroupPaymentPageComponent: ['AuthGuard', 'OrderValidateGuard'],
  OrderStatusPageComponent: ['AuthGuard'],
  OrderDetailsPageComponent: ['AuthGuard'],
  AccountPageComponent: ['AuthGuard'],
  OrdersPageComponent: ['AuthGuard'],
  ChecksPageComponent: ['AuthGuard'],
  CheckDetailsPageComponent: ['AuthGuard'],
  PatChooseAmountPageComponent: ['AuthGuard', 'PatCheckGuard'],
  PatPaymentPageComponent: ['AuthGuard', 'PatCheckGuard'],
  PatPaymentConfirmationPageComponent: ['AuthGuard', 'PatCheckGuard'],
};
const PAY_AT_TABLE_OVERVIEW_GUARDS = ['AuthGuard'] as const;

export interface AdaptCompiledSpecOptions {
  /** Page subset (studio preview). Null = all pages. */
  pageKey?: string | null;
}

export function toCompiledSpecFromApiResponse(
  response: ContentBuilderSpecApiResponse,
): ContentBuilderCompiledSpec {
  return {
    params: response.params,
    blocs: response.blocs,
    pages: response.pages,
    modals: response.modals,
  };
}

/**
 * Transforms a compiled CB2 spec into dood-front format (pages, blocks, params).
 * Does not resolve $param / $cases — that is resolveBoundValue's job at render time.
 */
export function adaptCompiledSpecToDoodRuntime(
  compiled: ContentBuilderCompiledSpec,
  options: AdaptCompiledSpecOptions = {},
): ContentBuilderSpecRuntimePayload {
  const { paramValues: rawParamValues, paramCatalog } = extractParamCatalog(compiled.params ?? {});
  const paramValues = materializeColorPaletteParamValues(rawParamValues, paramCatalog);

  const blocs = compiled.blocs ?? {};
  const pagesMap = compiled.pages ?? {};
  const modalsMap = compiled.modals ?? {};
  const pageKey = options.pageKey?.trim() || null;

  const blocIds = pageKey
    ? collectBlocIdsForPageKey(pagesMap, blocs, pageKey)
    : new Set(Object.keys(blocs));

  for (const modal of Object.values(modalsMap)) {
    const root = modal?.root;
    if (typeof root === 'string' && root) {
      for (const id of collectBlocIdsFromRoots(blocs, [root])) {
        blocIds.add(id);
      }
    }
  }

  const blocks: IBlockConfig[] = [];
  for (const [id, bloc] of Object.entries(blocs)) {
    if (!blocIds.has(id)) continue;
    const mapped = transformBloc(id, bloc);
    if (mapped) blocks.push(mapped);
  }
  pruneMissingBlockRefs(blocks);

  const pages = transformPages(pagesMap, blocs);
  const modals = transformModals(modalsMap);
  const userInterface = buildUserInterfaceFromParams(paramValues);

  return {
    pages,
    blocks,
    modals,
    ...(userInterface ? { user_interface: userInterface } : {}),
    paramValues,
    paramCatalog,
  };
}

/**
 * Projects CB2 `ui.colors` onto the legacy marketplace shape consumed by
 * `applyMarketplaceUserInterface` (`--ui-color-*` CSS variables).
 */
function buildUserInterfaceFromParams(
  paramValues: Record<string, unknown>,
): Partial<IMarketplaceUserInterface> | undefined {
  const colors = mapColorsParam(paramValues['ui.colors']);
  if (!colors) {
    return undefined;
  }
  return { colors };
}

function extractParamCatalog(paramsDef: Record<string, { type?: string; value?: unknown }>): {
  paramValues: Record<string, unknown>;
  paramCatalog: Record<string, { type?: string }>;
} {
  const paramValues: Record<string, unknown> = {};
  const paramCatalog: Record<string, { type?: string }> = {};
  for (const [key, def] of Object.entries(paramsDef)) {
    paramCatalog[key] = { type: def?.type };
    if (def?.value !== undefined) {
      paramValues[key] = def.value;
    }
  }
  return { paramValues, paramCatalog };
}

function collectBlocIdsForPageKey(
  pagesMap: Record<string, { root?: string; parent?: string }>,
  blocs: Record<string, ContentBuilderCompiledBloc>,
  pageKey: string,
): Set<string> {
  if (!pagesMap[pageKey]) {
    return new Set(Object.keys(blocs));
  }

  const pageKeys = collectPageSubtreeKeys(pagesMap, pageKey);
  const rootIds = [...pageKeys]
    .map(k => pagesMap[k]?.root)
    .filter((id): id is string => typeof id === 'string');

  let ancestor = pagesMap[pageKey]?.parent?.trim();
  while (ancestor && pagesMap[ancestor]) {
    pageKeys.add(ancestor);
    const root = pagesMap[ancestor]?.root;
    if (typeof root === 'string') rootIds.push(root);
    ancestor = pagesMap[ancestor]?.parent?.trim();
  }

  return collectBlocIdsFromRoots(blocs, rootIds);
}

function collectPageSubtreeKeys(
  pagesMap: Record<string, { parent?: string }>,
  rootKey: string,
): Set<string> {
  const result = new Set([rootKey]);
  for (const [key, page] of Object.entries(pagesMap)) {
    if (page.parent?.trim() === rootKey) {
      for (const k of collectPageSubtreeKeys(pagesMap, key)) result.add(k);
    }
  }
  return result;
}

function collectBlocIdsFromRoots(
  blocs: Record<string, ContentBuilderCompiledBloc>,
  rootIds: string[],
): Set<string> {
  const result = new Set<string>();
  const visit = (id: string) => {
    if (!id || result.has(id)) return;
    result.add(id);
    const bloc = blocs[id];
    if (!bloc?.slots) return;
    for (const items of Object.values(bloc.slots)) {
      if (!Array.isArray(items)) continue;
      for (const childId of items) {
        if (typeof childId === 'string') visit(childId);
      }
    }
  };
  for (const id of rootIds) visit(id);
  return result;
}

function transformPages(
  pagesMap: Record<string, ContentBuilderCompiledPage>,
  blocs: Record<string, ContentBuilderCompiledBloc>,
): IPageConfig[] {
  return Object.entries(pagesMap).map(([id, page]) => {
    const fullPath = urlToPath(page.url ?? '');
    const guards = resolvePageGuards(page, blocs);
    let path = fullPath;
    if (page.parent) {
      const parentPath = urlToPath(pagesMap[page.parent]?.url ?? '');
      if (parentPath && fullPath.startsWith(`${parentPath}/`)) {
        path = fullPath.slice(parentPath.length + 1);
      } else if (fullPath === parentPath) {
        path = '';
      }
    }
    return {
      id,
      path,
      block: page.root,
      ...(page.parent ? { parent: page.parent } : {}),
      ...(Array.isArray(page.locales) && page.locales.length > 0 ? { locales: page.locales } : {}),
      ...(guards.length > 0 ? { guards: [...guards] } : {}),
    };
  });
}

function resolvePageGuards(
  page: ContentBuilderCompiledPage,
  blocs: Record<string, ContentBuilderCompiledBloc>,
): readonly string[] {
  if (page.guards !== undefined) {
    return page.guards;
  }

  const componentType = page.root ? blocs[page.root]?.type : undefined;
  if (
    componentType === 'GenericPageComponent' &&
    urlToPath(page.url ?? '') === 'pay-at-table/overview'
  ) {
    return PAY_AT_TABLE_OVERVIEW_GUARDS;
  }

  return componentType ? (DEFAULT_PAGE_GUARDS_BY_COMPONENT_TYPE[componentType] ?? []) : [];
}

function transformModals(modalsMap: Record<string, { root?: string }>): DoodModalConfig[] {
  return Object.entries(modalsMap)
    .map(([handle, modal]) => {
      const block = modal?.root?.trim();
      if (!block) return null;
      return { handle, block };
    })
    .filter((entry): entry is DoodModalConfig => entry !== null);
}

function urlToPath(url: string): string {
  const s = String(url ?? '').trim();
  if (!s || s === '/') return '';
  return s.replace(/^\//, '');
}

function transformBloc(id: string, bloc: ContentBuilderCompiledBloc): IBlockConfig | null {
  const data: Record<string, unknown> = {};
  const props = cloneDeep(bloc.props ?? {});

  if (bloc.type === 'MosaicCardBlockComponent' || bloc.type === 'MosaicBlockComponent') {
  }

  for (const [key, value] of Object.entries(props)) {
    data[snakeToCamel(key)] = value;
  }

  for (const [slotKey, items] of Object.entries(bloc.slots ?? {})) {
    if (ROUTING_SLOTS.has(slotKey)) continue;
    const slotItems = transformSlotItems(items, bloc.type, slotKey);
    const camelKey = snakeToCamel(slotKey);
    if (Array.isArray(data[camelKey])) {
      data[camelKey] = [...(data[camelKey] as unknown[]), ...slotItems];
    } else {
      data[camelKey] = slotItems;
    }
  }

  const common = transformCommonData(bloc.data?.common);
  if (common) {
    data['common'] = common;
  }

  if (bloc.type === 'MosaicCardBlockComponent' || bloc.type === 'MosaicBlockComponent') {
  }

  return { id, type: bloc.type, data };
}

function transformCommonData(
  common: Record<string, unknown> | undefined,
): Record<string, unknown> | null {
  if (!common || typeof common !== 'object' || Array.isArray(common)) return null;

  const out: Record<string, unknown> = {};
  const cv = common['conditionalVisibility'];
  if (cv && typeof cv === 'object' && !Array.isArray(cv)) {
    const cvObj = cv as Record<string, unknown>;
    const expression = typeof cvObj['expression'] === 'string' ? cvObj['expression'].trim() : '';
    if (expression) {
      out['conditionalVisibility'] = {
        expression,
        ...(Array.isArray(cvObj['conditions']) && (cvObj['conditions'] as unknown[]).length > 0
          ? { conditions: cvObj['conditions'] }
          : {}),
      };
    }
  }

  if (Array.isArray(common['variables']) && (common['variables'] as unknown[]).length > 0) {
    out['variables'] = common['variables'];
  }

  return Object.keys(out).length > 0 ? out : null;
}

function transformSlotItems(
  items: unknown[] | undefined,
  blocType: string,
  slotKey: string,
): unknown[] {
  const ids = Array.isArray(items) ? items.filter(item => typeof item === 'string') : [];
  if (MOSAIC_BLOCK_TYPES.has(blocType) && slotKey === 'cards') {
    return ids.map((block, position) => ({ block, position }));
  }
  return ids;
}

function pruneMissingBlockRefs(blocks: IBlockConfig[]): void {
  const blockIds = new Set(blocks.map(b => b.id));
  for (const block of blocks) {
    if (!block.data || typeof block.data !== 'object') continue;
    for (const [key, value] of Object.entries(block.data as Record<string, unknown>)) {
      if (!Array.isArray(value)) continue;
      (block.data as Record<string, unknown>)[key] = value.filter(item => {
        if (typeof item === 'string' && item.startsWith('bloc_')) {
          return blockIds.has(item);
        }
        if (
          item &&
          typeof item === 'object' &&
          typeof (item as { block?: string }).block === 'string'
        ) {
          return blockIds.has((item as { block: string }).block);
        }
        return true;
      });
    }
  }
}

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

function cloneDeep<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(item => cloneDeep(item)) as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = cloneDeep(v);
  }
  return out as T;
}
